import type { EarthFaultLoopRunCreateRequest } from "../services/earthFaultLoop";
import { downloadRunJson } from "../services/runExport";
import { EarthFaultLoopForm } from "../components/EarthFaultLoopForm";
import { EarthFaultLoopResultPanel } from "../components/EarthFaultLoopResultPanel";
import { EarthFaultLoopResultSummary } from "../components/EarthFaultLoopResultSummary";
import { EarthFaultLoopWarningPanel } from "../components/EarthFaultLoopWarningPanel";
import { EARTH_FAULT_LOOP_WORDING, FaultLinkPanel } from "../components/FaultLinkPanel";
import { ProtectionSwitch } from "../components/ProtectionSwitch";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useEarthFaultLoop } from "../hooks/useEarthFaultLoop";
import { useSldElements } from "../hooks/useSldElements";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The earth-fault loop study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside a result-first
// column - summary, the two links (SLD, the fault run that gave Ze), warnings, traceability,
// then the detail tables. EOS-05 is delivered in parts (A20, A21, A33); this page is 05d,
// reached through the protection switch beside the starter coordination and breaker pages.
export function EarthFaultLoopPage() {
  const { calculate, reset, result, run, error, isPending, isError } = useEarthFaultLoop();
  // The active revision's single-line diagram, for the bus and feeder pickers (A27 (b), D17).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: EarthFaultLoopRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-earth-fault-loop-page>
      <ProtectionSwitch />
      <header>
        <p>EOS-05 · Protection coordination</p>
        <h1>Earth-fault loop impedance and disconnection</h1>
        <p>
          Check each final or distribution circuit of one LV board for automatic disconnection
          on an earth fault: the loop impedance Zs from the external impedance Ze of a stored
          earth-fault study of the same project revision (or a declared value) and the circuit's
          cable, read from a stored cable study or typed, against the declared protective
          device's operating current or maximum loop impedance within the required time, using
          the validated KES Electrical OS calculation service. Every limit is stated with its
          basis; nothing is assumed.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="earth-fault-loop-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="earth-fault-loop-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <EarthFaultLoopForm disabled={isPending} onSubmit={handleSubmit} sldElements={sldElements} />
          </details>
        </section>

        <section aria-labelledby="earth-fault-loop-results-heading" data-study-results>
          <h2 id="earth-fault-loop-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Checking the earth-fault loop…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <EarthFaultLoopResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              {run ? <FaultLinkPanel link={run.fault_link} wording={EARTH_FAULT_LOOP_WORDING} /> : null}
              <EarthFaultLoopWarningPanel result={result} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <EarthFaultLoopResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to check the earth-fault loop.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="earth-fault-loop-review-heading">
        <h2 id="earth-fault-loop-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm the linked earth-fault and cable studies, the external
          impedance and its basis, each device's operating current or maximum loop impedance
          against the manufacturer's data or the registered table, and the required
          disconnection time against the installation's rules before approval or issue. The
          protective conductor's thermal withstand, the touch voltage and any additional
          protection are not evaluated here.
        </p>
      </section>
    </main>
  );
}

export default EarthFaultLoopPage;
